<template>
  <div class="xy-lucky-wheel" ref="componentRef">
    <lucky-wheel
      :key="upKey"
      ref="luckyWheelRef"
      :width="wheelCfg.width"
      :height="wheelCfg.height"
      :prizes="wheelCfg.prizes"
      :blocks="wheelCfg.blocks"
      :buttons="wheelCfg.buttons"
      :defaultConfig="wheelCfg.defaultConfig"
      :defaultStyle="wheelCfg.defaultStyle"
      @end="endCallback" />
    <div class="check-box" :style="checkBoxStyle">
      <template v-if="showCustomCheck">
        <img :style="checkboxImgStyle" :src="checkSrc" @click="handleCheckChange" />
      </template>
      <template v-else>
        <input type="checkbox" id="myCheckbox" @change="handleCheckChange" />
        <label for="myCheckbox"></label>
      </template>
      <span class="check-label">{{ checkLabel }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { info, defaultProps } from './config'
import { LuckyWheel } from '@lucky-canvas/vue'
import { computeRatioPx } from '@/packages/plugins/utils'
import { useLucky } from '@/packages/components/hook/useLucky'
import useAppStore from '@/h5/stores/app'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const appStore = useAppStore ? useAppStore() : null

// 转盘配置
const wheelCfg = ref({
  width: '300px',
  height: '300px',
  prizes: [
    { fonts: [{ text: '0', top: '10%' }], background: 'rgba(233,232,254, 0.5)' },
    { fonts: [{ text: '1', top: '10%' }], background: 'rgba(184,197,242, 0.5)' },
    { fonts: [{ text: '2', top: '10%' }], background: 'rgba(233,232,254, 0.5)' },
    { fonts: [{ text: '3', top: '10%' }], background: 'rgba(184,197,242, 0.5)' },
    { fonts: [{ text: '4', top: '10%' }], background: 'rgba(233,232,254, 0.5)' },
    { fonts: [{ text: '5', top: '10%' }], background: 'rgba(184,197,242, 0.5)' }
  ],
  blocks: [{ padding: '13px', background: '#617df2' }],
  buttons: [
    { radius: '50%', background: '#617df2' },
    { radius: '45%', background: '#afc8ff' },
    {
      radius: '40%',
      background: '#869cfa',
      pointer: true,
      fonts: [{ text: '开始\n抽奖', top: '-20px' }]
    }
  ],
  defaultConfig: {
    offsetDegree: 0
  },
  defaultStyle: {
    fontColor: '#000',
    fontSize: '20px',
    fontStyle: 'Arial'
  }
})
/** 初始化转盘配置 */
const initWheel = () => {
  const fontSize = computeRatioPx(20)
  const buttonsRadius = computeRatioPx(props.buttonsRadius)
  const blocksPadding = computeRatioPx(13)
  // 转盘宽高
  const size = computeRatioPx(props.width)
  wheelCfg.value.width = size
  wheelCfg.value.height = size
  // 背景图
  wheelCfg.value.blocks = props.blocksSrc ? [{ imgs: [{ src: props.blocksSrc, width: '100%', height: '100%', rotate: true }] }] : [{ padding: blocksPadding, background: '#617df2' }]
  // 背景图二
  if (props.indicatorSrc) {
    wheelCfg.value.blocks.push({ imgs: [{ src: props.indicatorSrc, width: '100%', height: '100%' }] })
  }
  // 按钮图片
  if (props.buttonsSrc) {
    wheelCfg.value.buttons = [{ radius: buttonsRadius, imgs: [{ src: props.buttonsSrc, width: '100%', height: '100%', top: '-' + buttonsRadius }] }]
  } else {
    wheelCfg.value.buttons[2].fonts = [{ text: '开始\n抽奖', top: '-' + fontSize }]
  }
  /** 默认设置 */
  // 偏移角度
  wheelCfg.value.defaultConfig.offsetDegree = Number(props.offsetDegree) || 0
  // 旋转速度
  wheelCfg.value.defaultConfig.speed = Number(props.speed) || 20
  // 减速时间
  wheelCfg.value.defaultConfig.decelerationTime = Number(props.decelerationTime)
  // 开始时间
  wheelCfg.value.defaultConfig.accelerationTime = Number(props.accelerationTime)
  /** 默认样式 */
  // 字体大小
  wheelCfg.value.defaultStyle.fontSize = fontSize
}
/** 设置奖项 */
const setPrizes = (sortedAndMergedAwards, language) => {
  const prizes = []
  props.prizes.forEach((item, index) => {
    const top = computeRatioPx(item.top)
    const size = computeRatioPx(item.width)
    const languageItem = item.languages.find((item) => item.language === language)
    let src = languageItem?.src
    if (languageItem.srcFeMale && languageItem.srcMale) {
      src = appStore?.pageDataSource['data-source_userConstant']?.sex === 2 ? languageItem.srcFeMale : languageItem.srcMale
    } else if (languageItem.srcMale) {
      src = languageItem.srcMale
    }
    const id = sortedAndMergedAwards && sortedAndMergedAwards[index] ? sortedAndMergedAwards[index].id : 0
    const rewards = sortedAndMergedAwards && sortedAndMergedAwards[index] ? sortedAndMergedAwards[index].rewards : []
    prizes.push({
      id: id,
      rewards: rewards,
      imgs: [{ src, top, width: size, height: size }]
    })
  })
  // 奖项排序
  wheelCfg.value.prizes = prizes
}

const {
  componentRef,
  luckyWheelRef,
  upKey,
  checkLabel,
  checkSrc,
  endCallback,
  showCustomCheck,
  checkboxImgStyle,
  handleCheckChange,
  checkBoxStyle,
  start,
  stop,
  getConfig,
  startRoulette,
  againRoulette,
  closeDialog,
  refreshMoneyBalances
} = useLucky(props, wheelCfg, initWheel, setPrizes)

defineExpose({
  start,
  stop,
  getConfig,
  startRoulette,
  againRoulette,
  closeDialog,
  refreshMoneyBalances
})
</script>

<style lang="scss" scoped>
.xy-lucky-wheel {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.check-box {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 45px;
  margin-top: 40px;
  box-sizing: border-box;
  .check-label {
    color: #fff;
  }
  #myCheckbox {
    display: none;
  }
  #myCheckbox + label::before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 10px;
    background: transparent; /* 默认背景颜色 */
    border-radius: 3px; /* 圆角 */
    vertical-align: middle;
    border: 1px solid #beabec;
  }

  /* 当复选框被选中时改变背景和添加勾选符号 */
  #myCheckbox:checked + label::before {
    background: transparent; /* 选中时的背景颜色 */
    content: '✓';
    color: #beabec; /* 勾选符号颜色 */
    font-size: 16px;
    text-align: center;
    line-height: 14px; /* 调整勾选符号的位置 */
  }
}
</style>
